import {cloneDeep} from 'lodash-es';
import { ISectionLayoutItem, IServiceItem, IBooking, IServiceLayoutItem, ITableItem, ITableItemAbstract, IEvent, IDiarySchedule } from 'shared-types/index';
import ObjectCopyService from "external-services/object-copy-service/index";
import BookingLookupService from "external-services/booking-lookup-service/index";
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import DateUtilsService from 'external-services/date-utils-service';

/**
 * This service is used to notify the backend when bookings are made so it can send emails sms confirmations etc
 */

const NS = 'BookingsAffectedService';

class BookingsAffectedService {

  private scheduleService: any;

  public setup(
    scheduleService: any
  ): void {
    this.scheduleService = scheduleService;
  }

  public checkDateRange(fromDate: Date, toDate: Date): Promise<{
    timeEffected: boolean;
    tableEffected: boolean;
    booking: IBooking;
  }[]> {
    const promises = [];
    let totalDays: number = DateUtilsService.daysBetweenDates(fromDate, toDate);
    totalDays += 1;
    for (let i = 0; i < totalDays; i++) {
      const testDate = new Date(fromDate.getTime());
      testDate.setDate(testDate.getDate() + i);
      promises.push(this.getErrorByDate(testDate));
    }
    return Promise.all(promises).then((results: {
      timeEffected: boolean;
      tableEffected: boolean;
      booking: IBooking;
    }[][]) => {
      return [].concat(...results);
    });
  }

  public checkSchedule(orgSchedule: IDiarySchedule, newSchedule: IDiarySchedule, effectDate: Date, bookingsToCheck: IBooking[] = null): Promise<{
    timeEffected: boolean;
    tableEffected: boolean;
    booking: IBooking;
  }[]> {
    return new Promise((resolve) => {
      if (bookingsToCheck) {
        // bookings to check have been passed in
        newSchedule.services = this.scheduleService.orderServices(newSchedule.services);
        newSchedule = this.scheduleService.addServicesSections(newSchedule, effectDate);
        this.getTablesByService(newSchedule, effectDate).then(() => {
          this.checkBookings(newSchedule, bookingsToCheck).then((results: {
            timeEffected: boolean;
            tableEffected: boolean;
            booking: IBooking;
          }[]) => {
            resolve(results);
          });
        });

      } else {
        // find bookings that are effected
        this.findBookings(orgSchedule, newSchedule, effectDate).then((bookingByDates: {
          [key: string]: IBooking[];
        }) => {
          const promises: Promise<any>[] = [];
          // loop over the date and get the schedule for that date
          for (const key in bookingByDates) {
            const bookings: IBooking[] = bookingByDates[key];
            if (bookings.length) {
              const scheduleDate = new Date(key);
              const prom = this.scheduleService.addDateInfoToSchedule(scheduleDate, ObjectCopyService.angularSafeCopy(newSchedule))
                .then((scheduleForDate: IDiarySchedule) => {
                  const dateKey = DateUtilsService.dateToIdStr(scheduleForDate.services[0].startTime);
                  // find effected bookings for the date
                  return this.getTablesByService(scheduleForDate, effectDate).then(() => {
                    return this.checkBookings(scheduleForDate, bookingByDates[dateKey]);
                  });
                });
              promises.push(prom);
            }
          }
          Promise.all(promises).then((effectedBookingsList: {
            timeEffected: boolean;
            tableEffected: boolean;
            booking: IBooking;
          }[][]) => {

            const effectedBookings = [].concat(...effectedBookingsList);
            if (effectedBookings.length) {
              resolve(effectedBookings);
            } else {
              //there are no bookings to that are effected
              resolve(null);
            }
          });
        });

      }

    });
  }

  public checkLayout(removedTables: ITableItemAbstract[], changedTables: ITableItemAbstract[], effectDate: Date, bookingsToCheck: IBooking[] = null): Promise<IBooking[]> {
    return new Promise((resolve) => {
      if (!bookingsToCheck) {
        BookingLookupService.getFromDate(effectDate).then((bookings: IBooking[]) => {
          const effectedBookings: IBooking[] = this.checkBookingsLayout(removedTables, changedTables, bookings);
          resolve(effectedBookings);
        });
      } else {
        const effectedBookings: IBooking[] = this.checkBookingsLayout(removedTables, changedTables, bookingsToCheck);
        resolve(effectedBookings);
      }
    });

  }

  public checkEvent(newEvent: IEvent, oldEvent: IEvent): Promise<{
    timeEffected: boolean;
    tableEffected: boolean;
    booking: IBooking;
  }[] | boolean> {
    return new Promise((resolve) => {
      const promises = [];
      promises.push(this.checkBookingsEvent(newEvent));
      if (oldEvent) {
        const newEventDate = DateUtilsService.dateTimeStrToDate(newEvent.date as string);
        const oldEventDate = oldEvent.date;
        if (!DateUtilsService.isOnSameDate(newEventDate, oldEventDate as Date)) {
          promises.push(this.checkBookingsEvent(oldEvent));
        }
      }
      Promise.all(promises).then((effectedBookingsList: {
        timeEffected: boolean;
        tableEffected: boolean;
        booking: IBooking;
      }[][]) => {
        const effectedBookings = [].concat(...effectedBookingsList);
        if (effectedBookings.length) {
          resolve(effectedBookings);
        } else {
          //there are no bookings to that are effected
          resolve(false);
        }
      });

    });
  }

  public checkDay(date: Date, schedule: IDiarySchedule, bookings: IBooking[]): Promise<{
    timeEffected: boolean;
    tableEffected: boolean;
    booking: IBooking;
  }[]> {
    return this.getTablesByService(schedule, date).then(() => {
      return this.checkBookings(schedule, bookings);
    });
  }

  /*************************************** PRIVATE *****************************************/

  private checkBookingsEvent(eventObj: IEvent): Promise<{
    timeEffected: boolean;
    tableEffected: boolean;
    booking: IBooking;
  }[]> {
    let schedule: IDiarySchedule;
    const dateOfEvent: Date = typeof eventObj.date === 'string' ?
      DateUtilsService.dateTimeStrToDate(eventObj.date)
      :
      eventObj.date;

    return ScheduleLookupService.getSchedule(dateOfEvent).then((scheduleForDate: IDiarySchedule) => {
      schedule = scheduleForDate;
    }).then(() => {
      return this.getTablesByService(schedule, dateOfEvent);
    }).then(() => {
      return BookingLookupService.getBySchedule(schedule);
    }).then((result: {
      schedule: IDiarySchedule;
      bookings: IBooking[];
    }) => {
      return this.checkBookings(schedule, result.bookings);
    });
  }

  private checkBookingsLayout(removedTables: ITableItemAbstract[], changedTables: ITableItemAbstract[], bookings: IBooking[]): IBooking[] {
    if (bookings && bookings.length) {
      const effectedBookings: IBooking[] = [];
      const removedTablesIds: string[] = removedTables.map((table: ITableItemAbstract) => {
        return table._id;
      });
      const changedTablesIds: string[] = changedTables.map((table: ITableItemAbstract) => {
        return table._id;
      });
      const totalBookings: number = bookings.length;
      for (let i = totalBookings - 1; i >= 0; i--) {
        const booking: IBooking = bookings[i];
        if (booking.tables) {
          for (let t = booking.tables.length - 1; t >= 0; t--) {
            const table: ITableItem = booking.tables[t];
            const removeIndex: number = removedTablesIds.indexOf(table._id);
            if (removeIndex !== -1) {
              // that booking is using one of the tables that have been removed
              effectedBookings.push(booking);
              break;
            }
            const changeIndex: number = changedTablesIds.indexOf(table._id);
            if (changeIndex !== -1) {
              // that booking is using one of the tables that have been changed
              const changedTable = changedTables[changeIndex];
              if (booking.people < changedTable.minCapacity || booking.people > changedTable.capacity) {
                // the booking no longer fits on the table
                effectedBookings.push(booking);
                break;
              }
            }
          }
        }

      }
      return effectedBookings;
    } else {
      return [];
    }
  }

  private getErrorByDate(date: Date): Promise<{
    timeEffected: boolean;
    tableEffected: boolean;
    booking: IBooking;
  }[]> {
    return ScheduleLookupService.getSchedule(date).then((schedule: IDiarySchedule) => {
      return BookingLookupService.getBySchedule(schedule).then((results: {
        bookings: IBooking[];
        schedule: IDiarySchedule;
      }) => {
        return this.checkDay(date, schedule, results.bookings);
      });
    });
  }

  private getTablesByService(schedule: IDiarySchedule, effectDate: Date): Promise<void> {
    return new Promise((resolve: any) => {

      const promises: Promise<boolean>[] = [];
      schedule.services.forEach((service: IServiceItem, i: number) => {
        const layoutIds: string[] = service.sectionLayouts.map((sectionLayout: ISectionLayoutItem) => {
          return sectionLayout.layoutId;
        });
        promises.push(LayoutsService.getByLayouts(layoutIds, effectDate, i).then((results: {
          returnRef: number;
          tablesArray: ITableItemAbstract[];
        }) => {
          const tables = results.tablesArray;
          const serviceNum = results.returnRef;
          const service = schedule.services[serviceNum];
          service.tablesIdsList = tables.map((table: ITableItemAbstract) => {
            return table._id;
          });
          return true;
        }));
      });
      Promise.all(promises).then(() => {
        resolve();
      });

    });
  }

  private findBookings(orgSchedule: IDiarySchedule, newSchedule: IDiarySchedule, effectDate: Date): Promise<{
    [key: string]: IBooking[];
  }> {

    // TODO Check if schedule.date is used otherwise remove it
    //if (newSchedule.date) {
    // the schedule is only for one date
    // } else {
    // the schedule for a day of the week
    // get all future bookings
    return BookingLookupService.getFromDate(effectDate).then((bookings: IBooking[]) => {
      return this.orderBookingsByScheduleDate(newSchedule, bookings);
    });
    //  }
  }

  private orderBookingsByScheduleDate(newSchedule: IDiarySchedule, bookings: IBooking[]): {
    [key: string]: IBooking[];
  } {
    const bookingByDates: {
      [key: string]: IBooking[];
    } = {};
    bookings = bookings.sort((a: IBooking, b: IBooking) => {
      return b.time.getTime()- a.time.getTime();
    });

    bookings.forEach((booking: IBooking) => {
      let fallsInSchedule: boolean = true;
      if (!newSchedule.date) {
        // the schedule for a day of the week
        const dayOfBooking: number = booking.time.getDay();
        if (Number(dayOfBooking) !== Number(newSchedule.dayNum) && !(Number(dayOfBooking) === Number(newSchedule.dayNum) + 1 && DateUtilsService.isTimeSmallerThanDayEnd(booking.time))) {
          // the booking does not fall in this schedule
          fallsInSchedule = false;
        }
      }
      if (fallsInSchedule) {
        // the booking will be effected by this change
        const scheduleDate = cloneDeep(booking.time);
        if (DateUtilsService.isTimeSmallerThanDayEnd(scheduleDate)) {
          // if the date is before 4 the booking uses the schedule for the day before
          scheduleDate.setDate(scheduleDate.getDate() - 1);
        }
        const scheduleDateId = DateUtilsService.dateToIdStr(scheduleDate);
        // create a new array of booking for this date
        if (!bookingByDates[scheduleDateId]) {
          bookingByDates[scheduleDateId] = [];
        }
        bookingByDates[scheduleDateId].push(booking);
      }
    });
    return bookingByDates;
  }

  private checkBookings(schedule: IDiarySchedule, bookings: IBooking[]): Promise<{
    timeEffected: boolean;
    tableEffected: boolean;
    booking: IBooking;
  }[]> {

    const serviceIds: string[] = schedule.services.map((service: IServiceItem) => {
      return service._id;
    });

    return new Promise((resolve) => {

      // check bookings
      const effectedBookings: {
        timeEffected: boolean;
        tableEffected: boolean;
        booking: IBooking;
      }[] = [];
      if (!bookings) {
        resolve(effectedBookings);
        return;
      }

      const lastBookings = bookings.length - 1;
      for (let i = lastBookings; i >= 0; i--) {
        let timeEffected = false;
        let tableEffected = false;
        const booking: IBooking = bookings[i];

        if (booking.status && booking.status.statusType !== 'cancelled') {
          let inServiceLayoutNum: number | boolean = false;
          // eslint-disable-next-line no-var
          var serviceLayout: IServiceLayoutItem;
          for (let s = schedule.serviceLayouts.length - 1; s >= 0; s--) {
            serviceLayout = schedule.serviceLayouts[s];
            const bookingMinOfDay: number = this.getMinOfDay(booking.time);
            const serviceStartMin: number = this.getMinOfDay(new Date(serviceLayout.startTime));
            let serviceEndMin: number = this.getMinOfDay(new Date(serviceLayout.endTime));
            if (new Date(serviceLayout.endTime).getDate() > new Date(serviceLayout.startTime).getDate()) {
              serviceEndMin += (60 * 24);
            }
            let serviceIdMatch = true;
            let sectionIdMatch = true;
            if (booking.serviceId) {
              serviceIdMatch = (serviceLayout.serviceId === booking.serviceId) ? true : false;
            }
            if (booking.tables && booking.tables.length) {
              sectionIdMatch = (serviceLayout.sectionId === booking.tables[0].sectionId) ? true : false;
            }

            if (bookingMinOfDay >= serviceStartMin && bookingMinOfDay <= serviceEndMin && serviceIdMatch && sectionIdMatch) {
              // The service layout the booking is in
              inServiceLayoutNum = s;
              break;
            }
          }

          if (schedule.isOpen === false) {
            // the venue is now closed on this date
            timeEffected = true;
          } else if (inServiceLayoutNum === false) {
            // the booking falls out side of the new service times
            timeEffected = true;
          }
          if (inServiceLayoutNum !== false) {
            // check to make sure all the tables are still available
            serviceLayout = schedule.serviceLayouts[inServiceLayoutNum];
            const serviceIdIndex = serviceIds.indexOf(serviceLayout.serviceId);
            const allowedTableIds = schedule.services[serviceIdIndex].tablesIdsList;
            if (booking.tables && booking.tables.length) {
              for (let t = 0; t < booking.tables.length; t++) {
                const table = booking.tables[t];
                const tableId = table._id;

                const tableIndex = allowedTableIds.indexOf(tableId);
                if (tableIndex === -1) {
                  // the table was not found
                  tableEffected = true;
                  break;
                }
                if (serviceLayout.sectionId !== table.sectionId && t === 0) { // @todo this case doesn't need, if this case is true , above sectionIdMatch as false, so inServiceLayoutNum is false, and then  inServiceLayoutNum === false => timeEffected
                  // the first table is not in use in the section
                  tableEffected = true;
                  break;
                }
              }
            }

          }
          if (timeEffected || tableEffected) {
            effectedBookings.push({
              timeEffected: timeEffected,
              tableEffected: tableEffected,
              booking: booking
            });
          }
        }
      }
      resolve(effectedBookings);
    });
  }

  private getMinOfDay(time: Date): number {
    if (time instanceof Date) {
      let minOfDay = (time.getHours() * 60) + time.getMinutes();
      if (DateUtilsService.isTimeSmallerThanDayEnd(time)) {
        minOfDay += 24;
      }
      return minOfDay;
    }
    return time;
  }

  public assignServiceForInvalidBooking(booking: IBooking): Promise<void> {

    return ScheduleLookupService.getSchedule(booking.time)
      .then((schedule: IDiarySchedule) => {

        const result = this.getBookingService(booking, schedule);

        if (result && result.serviceId && result.time) {
          booking.serviceId = result.serviceId;
          booking.time = new Date(result.time);
        }
      });
  }

  /**
   * Returns a booking's serviceId and time, comparing it with the given schedule.
   */
  private getBookingService(booking: IBooking, schedule: IDiarySchedule): {
    serviceId: string;
    time: string;
  } {
    const serviceLayouts: IServiceLayoutItem[] = schedule.serviceLayouts;
    const serviceLayoutsOnBooking = serviceLayouts.filter((data: IServiceLayoutItem) => booking.serviceId === data.serviceId);
    const serviceIdMatch: string = this.getServiceByTime(booking.time, serviceLayouts);

    return {
      serviceId: serviceLayoutsOnBooking.length > 0 ? serviceLayoutsOnBooking[0].serviceId : serviceIdMatch,
      time: booking.time.toString(),
    }
  }

  private getServiceByTime(time: Date, serviceLayouts: IServiceLayoutItem[]): string {
    const sl: IServiceLayoutItem = serviceLayouts.find((sl: IServiceLayoutItem) => {
      const bookingMinOfDay = this.getMinOfDay(time);
      const serviceStartMin = this.getMinOfDay(new Date(sl.startTime));
      const serviceEndMin = this.getMinOfDay(new Date(sl.endTime));

      if (bookingMinOfDay >= serviceStartMin && bookingMinOfDay < serviceEndMin) {
        return true;
      }else{
        return false;
      }
    });

    return sl ? sl.serviceId : null;
  }
}

const instance = new BookingsAffectedService();
export default instance as BookingsAffectedService;
